import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../slices/authSlice';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import {
    EyeIcon,
    EyeSlashIcon,
    UserIcon,
    CheckCircleIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { trackUserLogin } from '../utils/analytics';

const API_BASE = 'https://cvanalyzerback.osc-fr1.scalingo.io/api';

const Login = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const afterAuthPath = searchParams.get('redirect') === '/subscription' ? '/subscription' : '/upload';
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            const res = await fetch(`${API_BASE}/users/google-auth`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: credentialResponse.credential }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Erreur Google');
            trackUserLogin(data.user);
            dispatch(loginSuccess({ token: data.token, refreshToken: data.refreshToken, user: data.user }));
            navigate(afterAuthPath);
        } catch (err) {
            setError(err.message);
        }
    };

    const { email, password } = formData;

    const regexEmail = /^[^@\s]{2,30}@[^@\s]{2,30}\.[^@\s]{2,5}$/;
    const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/;

    const validateEmail = (email) => {
        if (!regexEmail.test(email)) {
            setEmailError("L'adresse email n'est pas valide.");
        } else {
            setEmailError('');
        }
    };

    const validatePassword = (password) => {
        if (!regexPassword.test(password)) {
            setPasswordError('Le mot de passe doit comporter au moins 8 caractères, une majuscule et un chiffre.');
        } else {
            setPasswordError('');
        }
    };

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Supprimer les erreurs quand l'utilisateur tape
        if (e.target.name === 'email' && emailError) {
            setEmailError('');
        }
        if (e.target.name === 'password' && passwordError) {
            setPasswordError('');
        }
    };

    const onBlur = e => {
        // Valider seulement quand l'utilisateur quitte le champ
        if (e.target.name === 'email' && e.target.value) {
            validateEmail(e.target.value);
        }
        if (e.target.name === 'password' && e.target.value) {
            validatePassword(e.target.value);
        }
    };

    const onSubmit = async e => {
        e.preventDefault();
        setError(null);
        setSubmitted(false);
        setIsSubmitting(true);

        if (emailError || passwordError) {
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();
            if (response.ok) {
                trackUserLogin(data.user);
                dispatch(loginSuccess({ token: data.token, refreshToken: data.refreshToken, user: data.user }));
                navigate(afterAuthPath);
            } else {
                setError(data.msg || 'Erreur lors de la connexion.');
            }
        } catch (error) {
            setError('Erreur lors de la connexion.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 pt-16 flex items-center justify-center p-4">
            <Helmet>
                <title>Connexion | CV Analyse</title>
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href="https://www.cv-analyse.fr/login" />
            </Helmet>
            {!submitted ? (
                <div className="card-surface p-6 sm:p-8 max-w-md w-full">
                    <div className="mb-6">
                        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
                            Connexion
                        </h1>
                        <p className="text-slate-600 text-sm leading-relaxed mt-2">
                            Accédez à votre compte CV Analyse
                        </p>
                    </div>

                    {/* Connexion Google */}
                    <div className="mb-6">
                        <div className="flex justify-center">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => setError('Erreur lors de la connexion Google')}
                                text="signin_with"
                                shape="rectangular"
                                size="large"
                                width="100%"
                                locale="fr"
                            />
                        </div>
                        <div className="flex items-center my-6">
                            <div className="flex-1 border-t border-gray-200" />
                            <span className="px-4 text-sm text-gray-400">ou</span>
                            <div className="flex-1 border-t border-gray-200" />
                        </div>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="field-label">
                                Adresse email
                            </label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    required
                                    autoComplete="email"
                                    aria-invalid={Boolean(emailError)}
                                    aria-describedby={emailError ? 'email-error' : undefined}
                                    className={`field-input ${emailError ? 'border-red-500' : ''}`}
                                    placeholder="exemple@email.com"
                                />
                            {emailError && (
                                <div id="email-error" className="mt-2 flex items-center text-red-500 text-sm">
                                    <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                                    <span>{emailError}</span>
                                </div>
                            )}
                        </div>

                        {/* Mot de passe */}
                        <div>
                            <label htmlFor="password" className="field-label">
                                Mot de passe
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={password}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    required
                                    autoComplete="current-password"
                                    aria-invalid={Boolean(passwordError)}
                                    aria-describedby={passwordError ? 'password-error' : undefined}
                                    className={`field-input pr-11 ${passwordError ? 'border-red-500' : ''}`}
                                    placeholder="Votre mot de passe"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    aria-pressed={showPassword}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus-ring"
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="w-5 h-5" />
                                    ) : (
                                        <EyeIcon className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            {passwordError && (
                                <div id="password-error" className="mt-2 flex items-start text-red-500 text-sm">
                                    <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                                    <span>{passwordError}</span>
                                </div>
                            )}
                        </div>

                        {/* Bouton de connexion */}
                        <button
                            type="submit"
                            disabled={isSubmitting || emailError || passwordError}
                            className={`w-full ${
                                isSubmitting || emailError || passwordError
                                    ? 'bg-gray-400 cursor-not-allowed py-4 px-6 rounded-xl font-semibold flex items-center justify-center'
                                    : 'btn-primary'
                            } focus-ring`}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                    Connexion en cours...
                                </>
                            ) : (
                                'Se connecter'
                            )}
                        </button>

                        {/* Message d'erreur */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
                                <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                                <span className="text-red-600 text-sm">{error}</span>
                            </div>
                        )}
                    </form>

                        {/* Liens utiles - en dehors du formulaire pour éviter tout conflit */}
                        <div className="text-center pt-4 mt-4 border-t border-gray-200 space-y-3">
                            <div>
                                <a
                                    href="/reset-password-request"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        window.location.href = '/reset-password-request';
                                    }}
                                    className="text-blue-600 hover:text-blue-700 font-semibold transition-colors text-sm cursor-pointer"
                                >
                                    Mot de passe oublié ?
                                </a>
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">
                                    Pas encore de compte ?{' '}
                                    <Link 
                                        to={searchParams.get('redirect') === '/subscription' ? '/register?redirect=/subscription' : '/register'} 
                                        className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                                    >
                                        S'inscrire
                                    </Link>
                                </p>
                            </div>
                        </div>
                </div>
            ) : (
                <div className="card-surface p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-semibold mb-4 text-gray-900">Connexion réussie</h2>
                    <p className="text-gray-600 mb-6">
                        Vous pouvez maintenant analyser votre CV.
                    </p>
                    
                    {profile && (
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-6 border border-blue-100">
                            <div className="flex items-center justify-center mb-4">
                                <UserIcon className="w-8 h-8 text-blue-600 mr-3" />
                                <h3 className="text-lg font-semibold text-gray-800">{profile.username}</h3>
                            </div>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p><span className="font-semibold">Email :</span> {profile.email}</p>
                                <p><span className="font-semibold">Crédits :</span> {profile.credits}</p>
                            </div>
                        </div>
                    )}
                    
                    <Link
                        to="/upload"
                        className="btn-primary"
                    >
                        Analyser mon CV
                    </Link>
                </div>
            )}
        </div>
    );
};

export default Login;
